import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { AuthService } from '../../core/auth/services/auth.service';
import { UserRole } from '../../core/auth/models/profile';
import { CalendarService } from './calendar.service';
import { AllRegions } from '../data/regions';

/**
 * Service to manage filter state and track which filters are actively applied.
 * Centralizes all filter-related data and logic.
 */
@Injectable({
  providedIn: 'root',
})
export class FilterStateService {
  private readonly allRegions = [...AllRegions];

  // Track which filter keys are actively applied by the user
  private readonly appliedFilters = new Set<string>();

  filterData = {
    regions: [] as string[],
    markets: [] as string[],
    therapeuticAreas: [] as string[],
    start_month: '',
    end_month: '',
  };
  pristineFilters = { ...this.filterData };

  constructor(
    private readonly authService: AuthService,
    private readonly calendarService: CalendarService,
  ) {
    this.initializeFilters();
  }

  private initializeFilters(): void {
    const today = new Date();
    this.filterData.end_month =
      this.calendarService.getFormattedMonthYear(today);
    this.filterData.start_month = this.calendarService.getFormattedMonthYear(
      new Date(today.setFullYear(today.getFullYear() - 2)),
    );

    if (this.authService.dbUserData) {
      // Set regions from user data
      this.filterData.regions = this.authService.dbUserData.regions;
      this.filterData.markets = this.authService.dbUserData.markets;

      // If user is not TA_USER, grant access to all TAs
      if (
        this.authService.dbUserData.role !== UserRole.TA_USER ||
        this.authService.dbUserData.regions.includes('_ALL')
      ) {
        this.filterData.regions = [...this.allRegions];
      }
    }

    this.pristineFilters = { ...this.filterData };

    const sessionCacheKey = 'monthly-selected-range';
    // Load selected months from session storage
    const cachedData = sessionStorage.getItem(sessionCacheKey);
    if (cachedData) {
      const selectedRange = JSON.parse(cachedData) as {
        month: string;
        year: number;
        selected: boolean;
      }[];

      const startMonth = selectedRange[0];
      const endMonth = selectedRange.at(-1)!;

      this.filterData.start_month = this.calendarService.getFormattedMonthYear(
        new Date(
          startMonth.year,
          CalendarService.monthLabels.indexOf(startMonth.month),
        ),
      );
      this.filterData.end_month = this.calendarService.getFormattedMonthYear(
        new Date(
          endMonth.year,
          CalendarService.monthLabels.indexOf(endMonth.month),
        ),
      );
    }
  }

  get filterAsParams(): HttpParams {
    // Edge case: user has no db record. That means global view by default
    if (this.authService.dbUserData === null) {
      return new HttpParams().append('regions', this.allRegions.join(','));
    }

    let params = new HttpParams();
    Object.keys(this.filterData).forEach((key) => {
      const value = (this.filterData as any)[key];

      if (Array.isArray(value)) {
        if (value.length === 0) return;
        if (key === 'markets' && value.includes('_ALL')) {
          // If _ALL is selected, skip adding this param (backend treats missing param as all)
          return;
        }
        params = params.append(key, value.join(','));
      } else if (value) {
        params = params.append(key, value);
      }
    });

    // Edge case: no dbUser data, then hardcode every region
    if (!this.authService.dbUserData) {
      params = params.delete('regions');
      params = params.delete('markets');
      params = params.append('regions', this.allRegions.join(','));
    }

    return params;
  }

  /**
   * Marks a filter key as actively applied
   */
  applyFilter(key: string): void {
    this.appliedFilters.add(key);
  }

  /**
   * Removes a filter key from the applied set
   */
  clearFilter(key: string): void {
    this.appliedFilters.delete(key);
  }

  /**
   * Checks if a filter key is currently applied
   */
  isFilterApplied(key: string): boolean {
    return this.appliedFilters.has(key);
  }

  /**
   * Clears all applied filter keys
   */
  clearAllFilters(): void {
    this.appliedFilters.clear();
  }

  /**
   * Checks if any filter is currently applied
   */
  hasAnyFilterApplied(): boolean {
    return this.appliedFilters.size > 0;
  }

  /**
   * Returns all currently applied filter keys
   */
  getAppliedFilters(): string[] {
    return Array.from(this.appliedFilters);
  }
}
